import * as React from "react"
import { router } from "@inertiajs/react"
import { X } from "lucide-react"
import { user_role_path } from "@/lib/routes"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import AddPersonDialog from "./AddPersonDialog"
import type { Role, Member, OrgTreeNode } from "../types"

type Props = {
  role: Role
  members: Member[]
  orgTree: OrgTreeNode[]
  canAssign: boolean
}

// Who holds this role. One row per person; one chip per org node they hold it
// at, each chip removable in one click (a UserRole is always person + node).
export default function RolePeople({ role, members, orgTree, canAssign }: Props) {
  const [removingId, setRemovingId] = React.useState<string | null>(null)

  const remove = (userRoleId: string) => {
    setRemovingId(userRoleId)
    router.delete(user_role_path(userRoleId), {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setRemovingId(null),
    })
  }

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm">
          People
          <span className="ml-1.5 text-xs text-muted-foreground">{role.people_count}</span>
        </span>
        {canAssign && (
          <AddPersonDialog
            role={role}
            members={members}
            orgTree={orgTree}
            trigger={<Button type="button" variant="outline" size="sm">Add person</Button>}
          />
        )}
      </div>

      <div className="max-h-72 overflow-y-auto rounded-md border border-border bg-background">
        {role.people.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">No one holds this role yet.</p>
        ) : (
          <ul className="divide-y">
            {role.people.map((p) => (
              <li key={p.user_id} className="flex flex-col gap-1.5 px-3 py-2">
                <span className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-foreground">{p.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{p.email}</span>
                </span>
                <span className="flex flex-wrap gap-1">
                  {p.nodes.map((n) => (
                    <Badge
                      key={n.user_role_id}
                      variant="outline"
                      className="gap-1 border-primary/25 bg-primary/5 pr-1 font-medium"
                    >
                      {n.org_node_name}
                      {canAssign && n.revocable && (
                        <button
                          type="button"
                          aria-label={`Remove ${p.name} from ${n.org_node_name}`}
                          title="Remove"
                          disabled={removingId === n.user_role_id}
                          onClick={() => remove(n.user_role_id)}
                          className="rounded-full p-0.5 text-muted-foreground hover:bg-primary/10 hover:text-destructive cursor-pointer disabled:opacity-50"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </Badge>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
